import {
  defer,
  firstValueFrom,
  mergeAll,
  type Observable,
  Subject,
  switchMap,
  timeout,
} from 'rxjs'
import { tap } from 'rxjs/operators'
import type {
  JobsFeatureOptions,
  ListPublishedLegendsFn,
  PrimeJobProgressionProps,
  PromptArchiveJobFn,
} from '@/features/jobs/index'
import Env from '@/infra/env'
import { WorklayerJobAPI } from '@taxfyle/txf-api-client'
import { JobsApi } from '@/features/jobs/api/job-api'
import { JobGistStore } from '@/features/jobs/JobGistStore'
import { JobDataLoader } from '@/features/jobs/JobDataLoader'
import { SubmittedJobStore } from '@/features/jobs/details/SubmittedJobStore'
import { memoize } from '@/utils/memoize-util'
import {
  JobDetailsViewModel,
  type JobTypeDetailsViewModel,
} from '@/features/jobs/details/JobDetailsViewModel'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import { ProgressionStore } from '@/features/jobs/taxes/progression/ProgressionStore'
import { MiscJobDetailsViewModel } from '@/features/jobs/details/misc-type/MiscJobDetailsViewModel'
import { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { QuestionStore } from '@/features/jobs/questions/QuestionStore'
import { QuestionsDisplayViewModel } from '@/features/jobs/details/QuestionsDisplayViewModel'
import { ProgressionViewModel } from '@/features/jobs/details/tax-type/ProgressionViewModel'
import { ConsultationJobDetailsViewModel } from '@/features/jobs/details/consultation-type/ConsultationJobDetailsViewModel'
import { ConsultationInfoGatheringViewModel } from '@/features/jobs/details/consultation-type/info-gathering/ConsultationInfoGatheringViewModel'
import { JobDocumentsViewModel } from '@/features/jobs/details/JobDocumentsViewModel'
import ConsultationManagerViewModel from '@/features/jobs/details/consultation/ConsultationManagerViewModel'
import { JobBillingDetailsViewModel } from '@/features/jobs/details/JobBillingDetailsViewModel'
import { JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import { JobScopeChangesDisplayViewModel } from '@/features/jobs/details/JobScopeChangesViewModel'
import type { Id } from '@/infra/id'
import { LegendsApi } from './api/legend-api'
import { PublishedLegendStore } from './legends/PublishedLegendStore'
import { MessageApi } from '@/features/messages/api/message-api'
import { ConsultationStore } from './consultations/ConsultationStore'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { DefaultJobConversationViewModel } from './details/JobConversationViewModel'
import { SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { LegendSettingsStore } from '@/features/jobs/legends/LegendSettingsStore'
import {
  type CreateNewDraftOptions,
  DraftJobStore,
} from './drafting/DraftJobStore'
import { BookkeepingJobDetailsViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingJobDetailsViewModel'
import { BookkeepingViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingViewModel'
import { BookkeepingProgressionStore } from './bookkeeping/BookkeepingProgressionStore'
import { BookkeepingStore } from './bookkeeping/BookkeepingStore'
import { TaxJobConversationViewModel } from '@/features/jobs/details/tax-type/TaxJobConversationViewModel'
import { incomingJobMessageNotifications } from '@/features/jobs/details/incoming-job-message-notifications'
import { links } from '@/utils/link'
import type LegendSettings from '@/features/jobs/legends/LegendSettings'
import { InfoGatheringStore } from '@/features/jobs/details/tax-type/info-gathering/InfoGatheringStore'
import { TaxInfoGatheringViewModel } from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import { RatingsViewModel } from '@/features/jobs/details/ratings/RatingsViewModel'
import { RatingsStore } from '@/features/jobs/details/ratings/RatingsStore'
import { Section7216AcceptanceViewModel } from '@/features/jobs/details/tax-type/section-7216/Section7216AcceptanceViewModel'
import type { PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import type { DraftJob } from '@/features/jobs/drafting/DraftJob'
import { DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import { createQuestionnaireViewModelFactory } from '@/features/jobs/drafting/questionnaire/QuestionnaireViewModel'
import { QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import { DefaultQuestionAnswerEditorViewModelFactory } from '@/features/jobs/questions/QuestionAnswerEditorViewModelFactory'
import { DraftPersister } from '@/features/jobs/drafting/DraftPersister'
import { PreviousProviderStore } from '@/features/jobs/drafting/PreviousProviderStore'
import { createRequestedProviderSelectionViewModelFactory } from '@/features/jobs/drafting/requested-provider/RequestedProviderSelectionViewModel'
import {
  type CheckoutViewModelFactory,
  createCheckoutViewModelFactory,
} from '@/features/jobs/drafting/checkout/CheckoutViewModel'
import { createCheckoutBillingViewModelFactory } from '@/features/jobs/drafting/checkout/CheckoutBillingViewModel'
import { ArchiveJobDialogViewModel } from '@/features/jobs/ArchiveJobDialog/ArchiveJobDialogViewModel'
import { createJobListViewModelFactory } from '@/features/jobs/list/JobListViewModel'
import { AllJobsViewModel } from '@/features/jobs/list/AllJobsViewModel'
import { JobWidgetViewModel } from '@/features/jobs/job-widget/JobWidgetViewModel'
import { JobWidgetStore } from '@/features/jobs/job-widget/JobWidgetStore'
import { TxfJobViewModel } from './details/tax-type/txf/TxfJobViewModel'
import { TxfJobStore } from './details/tax-type/txf/TxfJobStore'
import { TxfWorklayerJobStore } from './details/tax-type/tax-prep-organizer/TxfWorklayerJobStore'
import { TaxPrepOrganizerWrapperViewModel } from './details/tax-type/tax-prep-organizer/TaxPrepOrganizerWrapperViewModel'
import { MeasureOneStore } from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneStore'
import { JobDetailsRelationManagerViewModel } from '@/features/jobs/details/JobDetailsRelationManagerViewModel'

/**
 * Function for fetching legend settings.
 */
export type FetchLegendSettingsFn = (
  id: Id,
  version: number,
) => Promise<LegendSettings | null>

/**
 * Function to acquire a job details view model.
 */
export type AcquireJobDetailsViewModelFn = (
  job: SubmittedJob,
  signal?: AbortSignal,
) => Promise<JobDetailsViewModel>

/**
 * Function to acquire a draft job view model.
 */
export type AcquireDraftJobViewModelFn = (
  job: DraftJob,
  signal?: AbortSignal,
) => Promise<DraftJobViewModel>

/**
 * Function to fetch the latest published Legend.
 */
export type FetchLatestPublishedLegendVersionFn = (
  id: Id,
) => Promise<PublishedLegend | null>

/**
 * Function to create a new draft job.
 */
export type CreateNewDraftJobFn = (
  options: CreateNewDraftOptions,
) => Promise<DraftJob>

/**
 * Function to acquire a tax prep organizer view model.
 */
export type AcquireTaxPrepOrganizerViewModelFn = (
  submittedJob: SubmittedJob,
  signal?: AbortSignal,
) => Promise<TaxPrepOrganizerWrapperViewModel>

export function InternalJobsFeature(options: JobsFeatureOptions) {
  const api = new JobsApi({
    token$: options.token$,
    baseURL: Env.WORK_DOTNET_API,
  })

  const acquireToken = async () => {
    try {
      return await firstValueFrom(options.token$.pipe(timeout(10_000)))
    } catch {
      return null
    }
  }

  const worklayerJobApi = new WorklayerJobAPI(acquireToken, Env.TXF_API)

  const txfWorklayerJobStore = new TxfWorklayerJobStore(worklayerJobApi)

  const messageApi = new MessageApi({
    token$: options.token$,
    baseURL: Env.MESSAGE_API,
  })

  const fetchLegendSettings: FetchLegendSettingsFn = (id, version) =>
    legendSettingsStore.fetch(id, version)

  const jobDataLoader = new JobDataLoader(api)
  const jobGistStore = new JobGistStore(api, jobDataLoader)
  const progressionStore = new ProgressionStore(api)
  const jobScopeChangeStore = new JobScopeChangeStore(api, options.primeUser)
  const questionStore = new QuestionStore(api)
  const consultationStore = new ConsultationStore(
    api,
    messageApi,
    options.getCurrentUser,
  )
  const draftJobStore = new DraftJobStore(
    api,
    jobDataLoader,
    options.primeWorkspace,
  )

  const bookkeepingStore = new BookkeepingStore(api)
  const bookkeepingProgressionStore = new BookkeepingProgressionStore(api)

  const submittedJobStore = new SubmittedJobStore(
    api,
    jobDataLoader,
    options.primeUser,
    options.primeWorkspace,
  )

  const searchableJobStore = new SearchableJobStore(
    api,
    options.getCurrentWorkspace,
    options.primeUser,
    fetchLegendSettings,
    (props) => legendSettingsStore.prime(props),
    (props) => progressionStore.prime(props),
    jobDataLoader.clear$,
  )

  const txfJobStore = new TxfJobStore(api)

  const jobArchived$ = draftJobStore.archived$

  const archiveJobDialogViewModel = new ArchiveJobDialogViewModel(
    options.newSnackbar,
    draftJobStore,
  )

  const promptArchiveJob: PromptArchiveJobFn = (options) =>
    archiveJobDialogViewModel.prompt(options)

  const createJobListViewModel = createJobListViewModelFactory(
    options.getCurrentMember,
    options.unreadCount,
    searchableJobStore,
    options.createSnackbarRetryWrapper,
    promptArchiveJob,
    jobArchived$,
  )

  const previousProviderStore = new PreviousProviderStore(
    api,
    options.getCurrentMember,
    searchableJobStore,
  )

  const infoGatheringStore = new InfoGatheringStore(api, submittedJobStore)

  const ratingsStore = new RatingsStore(api)

  // We'll need to track which conversation is currently being viewed.
  // We can base this on which job's conversation VM is currently
  // active. Once we create a conversation VM, we'll include its active state.
  const activeConversationIdObservables$ = new Subject<Observable<Id | null>>()

  // We'll merge all the active conversation ID streams into a single stream,
  // since we know we'll only have 1 at a time. We also know that any currently
  // active conversation will emit a `null` on its' stream before the next
  // one activates.
  const activeJobConversationId$ =
    activeConversationIdObservables$.pipe(mergeAll())

  // Stream incoming messages into system notifications.
  const systemNotifications$ = incomingJobMessageNotifications({
    activeJobConversationId$,
    incomingMessage$: options.incomingMessages$,
    userActive$: options.userActive$,
    pageActivity$: options.pageActivity$,
    // Navigate to the messages view when interacting with a notification,
    // but only if the conversation is not currently active.
    onAction: ({ jobId, conversationIsActive }) =>
      !conversationIsActive &&
      options.navigate(links.jobDetailsMessages(jobId)),
  })

  const acquireJobDetailsViewModel: AcquireJobDetailsViewModelFn = memoize(
    async (job, signal) => {
      signal?.throwIfAborted()

      const {
        createDocumentsListViewModel,
        createDocumentLinkingViewModel,
        createNewBillingMethodDialogViewModel,
        createTransactionTableViewModel,
        createConversationViewModel,
      } = await ValueRetrievablePromise.props({
        createDocumentsListViewModel: options.documentsListViewModelFactory,
        createDocumentLinkingViewModel: options.documentLinkingViewModelFactory,
        createNewBillingMethodDialogViewModel:
          options.newBillingMethodDialogViewModelFactory,
        createTransactionTableViewModel:
          options.createTransactionTableViewModelFactory,
        createConversationViewModel: options.conversationViewModelFactory,
      })

      const questionsDisplayVm = new QuestionsDisplayViewModel(
        job,
        questionStore,
      )

      const newBillingMethodDialogVm = createNewBillingMethodDialogViewModel()

      const jobScopeChangeDisplayVm = new JobScopeChangesDisplayViewModel(
        job,
        jobScopeChangeStore,
        options.newSnackbar,
        newBillingMethodDialogVm,
        options.checkHasBillingMethod,
        options.getCurrentMember,
      )

      const transactionsTableVm = createTransactionTableViewModel({
        type: 'Job',
        jobId: job.id,
      })

      const jobBillingDetailsViewModel = new JobBillingDetailsViewModel(
        job,
        transactionsTableVm,
      )

      const createConsultationManagerVm = (job: SubmittedJob) =>
        new ConsultationManagerViewModel(
          job,
          consultationStore,
          createDocumentsListViewModel,
          options.createSnackbarErrorHandler,
          options.newSnackbar,
          options.getAddToCalendarDelegate,
          options.getExternalUrlDelegate,
        )

      // Bookkeeping
      const bookkeepingPlatformSelectionDialogViewModelFactory =
        await options.createBookkeepingPlatformSelectionDialogViewModelFactory

      // All job details VMs need a conversation VM, but they may wrap it
      // depending on the job type.
      const conversationVm = createConversationViewModel(
        () => job.conversationId,
      )
      // NOTE: This technically leaks, but we cache job details VMs infinitely
      // by design. If that changes, this needs to be revisited.
      activeConversationIdObservables$.next(
        conversationVm.activeConversationId$,
      )

      const jobDetailsRelationManagerViewModel =
        new JobDetailsRelationManagerViewModel(
          job.id,
          job.type,
          options.fetchRelationManager,
          options.getCurrentWorkspace,
        )

      // Create a type-specific VM.
      const typeVm = await job.match<PromiseLike<JobTypeDetailsViewModel>>({
        tax: async () => {
          const {
            createPersonalProfileEditorViewModel,
            createSpousalProfileEditorViewModel,
            createDependentProfileEditorViewModel,
            createTaxRefundMethodEditorViewModel,
            createBusinessProfileEditorViewModel,
          } = await ValueRetrievablePromise.props({
            createPersonalProfileEditorViewModel:
              options.createPersonalProfileEditorViewModel,
            createSpousalProfileEditorViewModel:
              options.createSpousalProfileEditorViewModel,
            createDependentProfileEditorViewModel:
              options.createDependentProfileEditorViewModel,
            createTaxRefundMethodEditorViewModel:
              options.createTaxRefundMethodEditorViewModel,
            createBusinessProfileEditorViewModel:
              options.createBusinessProfileEditorViewModel,
          })

          const txfJobVm = new TxfJobViewModel(
            options.getExternalUrlDelegate,
            job,
            txfJobStore,
          )

          const measureOneStore = new MeasureOneStore(api)

          const infoGatheringVm = new TaxInfoGatheringViewModel(
            job,
            infoGatheringStore,
            options.createSnackbarRetryWrapper,
            options.createSnackbarErrorHandler,
            createPersonalProfileEditorViewModel,
            options.fetchPersonalProfileVersionForJob,
            createSpousalProfileEditorViewModel,
            options.fetchSpousalProfileVersionForJob,
            createDependentProfileEditorViewModel,
            options.fetchDependentProfileVersionForJob,
            options.listDependents,
            options.promptDeleteDependent,
            createTaxRefundMethodEditorViewModel,
            options.fetchPersonalTaxRefundMethodForJob,
            options.fetchBusinessTaxRefundMethodForJob,
            options.listTaxRefundMethods,
            options.promptDeleteTaxRefundMethod,
            createBusinessProfileEditorViewModel,
            options.fetchBusinessProfileForJob,
            options.listBusinessProfiles,
            options.promptDeleteBusinessProfile,
            createDocumentsListViewModel,
            createDocumentLinkingViewModel,
            txfJobVm,
            options.getCurrentWorkspace,
            options.newSnackbar,
            measureOneStore,
          )
          const consultationVm = createConsultationManagerVm(job)
          const progressionVm = new ProgressionViewModel(
            job,
            createDocumentsListViewModel,
            progressionStore,
            options.createSnackbarErrorHandler,
            options.createSnackbarRetryWrapper,
          )
          const taxJobConversationVm = new TaxJobConversationViewModel(
            progressionStore,
            () => progressionVm.draftReviewVm.reviewDraft(),
            job,
            conversationVm,
          )

          const section7216AcceptanceVm = new Section7216AcceptanceViewModel(
            job,
            infoGatheringVm.isSpousalInfoRequested$,
            options.ensureSection7216Accepted,
          )

          return new TaxJobDetailsViewModel(
            job,
            progressionVm,
            consultationVm,
            taxJobConversationVm,
            infoGatheringVm,
            section7216AcceptanceVm,
            txfJobVm,
            defer(() => vm.termsOutcome$),
          )
        },
        consultation: () => {
          const consultationVm = createConsultationManagerVm(job)
          const jobConversationVm = new DefaultJobConversationViewModel(
            job,
            conversationVm,
          )

          const infoGatheringVm = new ConsultationInfoGatheringViewModel(
            job,
            consultationVm,
            submittedJobStore,
          )

          return ValueRetrievablePromise.fromValue(
            new ConsultationJobDetailsViewModel(
              job,
              consultationVm,
              jobConversationVm,
              infoGatheringVm,
            ),
          )
        },
        bookkeeping: () => {
          const bookkeepingVm = new BookkeepingViewModel(
            job,
            bookkeepingProgressionStore,
            bookkeepingStore,
            options.createSnackbarRetryWrapper,
            options.newSnackbar,
            bookkeepingPlatformSelectionDialogViewModelFactory,
            () => new URL(window.location.href),
          )
          const jobConversationVm = new DefaultJobConversationViewModel(
            job,
            conversationVm,
          )

          return ValueRetrievablePromise.fromValue(
            new BookkeepingJobDetailsViewModel(
              // NOTE: The `Ratings` VM depends on the type VM (this one),
              // but since these are lazy getters, it won't result in any error
              // as long as they are not called in the constructor.
              (): boolean => ratingsVm.isRatingsEnabled,
              (): boolean => ratingsVm.isRateable,
              job,
              jobConversationVm,
              bookkeepingVm,
            ),
          )
        },
        default: () => {
          // use Misc for now until we add the rest
          const jobConversationVm = new DefaultJobConversationViewModel(
            job,
            conversationVm,
          )
          return ValueRetrievablePromise.fromValue(
            new MiscJobDetailsViewModel(job, jobConversationVm),
          )
        },
      })

      const jobDocsVm = new JobDocumentsViewModel(
        createDocumentsListViewModel,
        createDocumentLinkingViewModel,
        job,
      )

      const ratingsVm = new RatingsViewModel(
        job,
        typeVm,
        ratingsStore,
        options.newSnackbar,
      )

      const vm: JobDetailsViewModel = new JobDetailsViewModel(
        job,
        typeVm,
        questionsDisplayVm,
        jobDocsVm,
        jobBillingDetailsViewModel,
        jobScopeChangeDisplayVm,
        options.getCurrentMember,
        options.ensureTermsAcceptance,
        options.startHandoff,
        ratingsVm,
        jobDetailsRelationManagerViewModel,
      )

      // If the job details VM wants to bounce the user away from job details, redirect back to the
      // home page.
      vm.lifecycle.manage(
        // TODO: Use exit method
        vm.bounce$.pipe(
          switchMap(() => options.pageExitMethod$),
          tap((exitMethod) => exitMethod.execute()),
        ),
      )

      return vm
    },
    {
      keySelector: ([job]) => job.id,
      promise: ([, sig]) => sig,
    },
  )

  // Legends.
  const legendsApi = new LegendsApi({
    token$: options.token$,
    baseURL: Env.LEGEND_API,
  })
  const publishedLegendStore = new PublishedLegendStore(legendsApi)
  const legendSettingsStore = new LegendSettingsStore(legendsApi)
  const listPublishedLegends: ListPublishedLegendsFn = (workspaceId, signal) =>
    publishedLegendStore.listPublishedLegends(workspaceId, signal)
  const fetchLatestPublishedLegendVersion: FetchLatestPublishedLegendVersionFn =
    (id) => publishedLegendStore.fetchLatestPublishedLegendVersion(id)

  // Job progression.
  const primeJobProgression = (props: PrimeJobProgressionProps) =>
    progressionStore.prime(props)

  // Draft job
  const archiveJob = (id: Id) => draftJobStore.archive(id)

  const acquireDraftJobViewModel: AcquireDraftJobViewModelFn = memoize(
    async (job: DraftJob, signal?: AbortSignal) => {
      signal?.throwIfAborted()

      const {
        createCardBillingMethodEditorViewModel,
        createBillingMethodListViewModel,
        createNewBillingMethodDialogViewModel,
        createApplePayViewModel,
      } = await ValueRetrievablePromise.props({
        createNewBillingMethodDialogViewModel:
          options.newBillingMethodDialogViewModelFactory,
        createBillingMethodListViewModel:
          options.createBillingMethodListViewModel,
        createCardBillingMethodEditorViewModel:
          options.createCardBillingMethodEditorViewModel,
        createApplePayViewModel: options.createApplePayViewModel,
      })

      const questionListVm = new QuestionListViewModel(
        new DefaultQuestionAnswerEditorViewModelFactory({
          getAsOfDate: () => job.createdAt,
          goBack: () => draftVm.outlineVm.goToPrevious(),
        }),
      )
      const persister = new DraftPersister(job, draftJobStore)

      const createQuestionnaireViewModel = createQuestionnaireViewModelFactory(
        job,
        questionListVm,
        questionStore,
      )

      const createRequestedProviderViewModel =
        createRequestedProviderSelectionViewModelFactory(
          job,
          previousProviderStore,
        )

      const createCheckoutBillingViewModel =
        createCheckoutBillingViewModelFactory(
          job,
          draftJobStore,
          createBillingMethodListViewModel,
          createCardBillingMethodEditorViewModel,
          createNewBillingMethodDialogViewModel,
          createApplePayViewModel,
          options.listCoupons,
          options.addCoupon,
          options.fetchCoupon,
          options.newSnackbar,
        )

      const createCheckoutViewModel: CheckoutViewModelFactory =
        createCheckoutViewModelFactory(
          job,
          draftJobStore,
          options.newSnackbar,
          options.ensureTermsAcceptance,
          createCheckoutBillingViewModel,
        )

      const draftVm: DraftJobViewModel = new DraftJobViewModel(
        job,
        persister,
        fetchLatestPublishedLegendVersion,
        createQuestionnaireViewModel,
        createRequestedProviderViewModel,
        createCheckoutViewModel,
        options.newSnackbar,
        options.getCurrentWorkspace,
      )
      return draftVm
    },
    {
      keySelector: ([job]) => job.id,
      promise: ([, sig]) => sig,
    },
  )

  const createNewDraftJob: CreateNewDraftJobFn = async (options) => {
    const draft = await draftJobStore.createNewDraft(options)
    // Preload the view model and view for the draft, as we're likely to be
    // viewing the draft after creating it. This also ensures that the caller
    // of this function is able to show a pending indicator until everything
    // is ready. On slower connections, this ensures that there won't be a delay
    // in showing the draft editor.
    await Promise.all([
      acquireDraftJobViewModel(draft),
      // Not a fan of doing it like this, but at least we've pushed this out
      // to the module boundary rather than doing it deeper inside the module.
      // Abstracting this further doesn't really buy us anything.
      import('./drafting/components/DraftJobView'),
    ])

    return draft
  }

  const allJobsVm = new AllJobsViewModel(createJobListViewModel)

  const jobWidgetStore = new JobWidgetStore(
    api.realtime$,
    questionStore.fetchQuestions,
    searchableJobStore.list,
    options.getCurrentWorkspace,
  )

  const jobWidgetVM = new JobWidgetViewModel(
    jobWidgetStore,
    options.generateSelfServiceMagicLink,
    options.getExternalUrlDelegate,
  )

  const acquireTaxPrepOrganizerViewModel: AcquireTaxPrepOrganizerViewModelFn =
    memoize(
      async (submittedJob, signal) => {
        signal?.throwIfAborted()

        const createConversationViewModel =
          await options.conversationViewModelFactory
        const conversationVm = createConversationViewModel(
          () => submittedJob.conversationId,
        )
        const taxJobConversationVm = new TaxJobConversationViewModel(
          progressionStore,
          () => undefined,
          submittedJob,
          conversationVm,
        )

        const txfJobVm = new TxfJobViewModel(
          options.getExternalUrlDelegate,
          submittedJob,
          txfJobStore,
        )

        const vm = new TaxPrepOrganizerWrapperViewModel(
          options.getExternalUrlDelegate,
          options.createSnackbarErrorHandler,
          options.newSnackbar,
          txfWorklayerJobStore,
          infoGatheringStore,
          submittedJob,
          txfJobVm,
          taxJobConversationVm,
        )

        await vm.fetch(signal)

        return vm
      },
      {
        keySelector: ([job]) => job.id,
        promise: ([, sig]) => sig,
      },
    )

  return {
    jobGistStore,
    submittedJobStore,
    txfJobStore,
    txfWorklayerJobStore,
    draftJobStore,
    questionStore,
    progressionStore,
    bookkeepingStore,
    bookkeepingProgressionStore,
    searchableJobStore,
    infoGatheringStore,
    acquireJobDetailsViewModel,
    acquireDraftJobViewModel,
    acquireTaxPrepOrganizerViewModel,
    listPublishedLegends,
    fetchLatestPublishedLegendVersion,
    fetchLegendSettings,
    primeJobProgression,
    createNewDraftJob,
    archiveJob,
    promptArchiveJob,
    archiveJobDialogViewModel,
    systemNotifications$,
    createJobListViewModel,
    jobArchived$,
    allJobsVm,
    jobWidgetVM,
  }
}
